
import { api } from './base';
import { getValidToken } from '@/features/auth';
import { config } from '@/shared/lib/config';
import { compressImageFile } from '@/shared/lib/compress-image';

export type ChangelogAudience = 'all' | 'staff' | 'requesters' | 'none';
export type ReleaseStatus = 'draft' | 'published' | 'unpublished' | 'deleted';
export type ChangelogRepo = 'orbit' | 'frontend';

export interface FeedEntry {
  id: string;
  text: string;
  details: string | null;
  detailsOpen: boolean;
  isHighlight: boolean;
}

export interface FeedSection {
  title: string;
  entries: FeedEntry[];
}

export interface FeedRelease {
  id: string;
  version: string | null;
  title: string;
  headline: string | null;
  body: string | null;
  publishedAt: string | null;
  isImportant: boolean;
  sections: FeedSection[];
}

export interface ChangelogState {
  hasUnread: boolean;
  importantRelease: FeedRelease | null;
  version: string | null;
}

export interface PoolEntry {
  id: string;
  repo: ChangelogRepo;
  sha: string;
  type: string;
  scope: string | null;
  subject: string;
  sourceText: string | null;
  text: string | null;
  sourceShortText: string | null;
  shortText: string | null;
  audience: ChangelogAudience;
  breaking: boolean;
  authoredAt: string;
  shippedAt: string | null;
  releaseId: string | null;
  isHighlight: boolean;
  detailsOpen: boolean;
  sortOrder: number;
  clonedFromId: string | null;
}

export interface AdminRelease {
  id: string;
  version: string | null;
  title: string;
  headline: string | null;
  body: string | null;
  isImportant: boolean;
  hideFromRequesters: boolean;
  status: ReleaseStatus;
  publishedAt: string | null;
  publishedBy: string | null;
  createdAt: string;
  entriesTotal: number;
  entriesVisible: number;
  entriesUnshipped: number;
  nextVersion: string | null;
}

export interface ChangelogImage {
  id: string;
  url: string;
  fileName: string;
}

const COMPRESSIBLE = new Set(['image/png', 'image/jpeg']);

export interface PoolFilters {
  repo?: ChangelogRepo;
  type?: string;
  audience?: ChangelogAudience;
  search?: string;
  includeHidden?: boolean;
  onlyShipped?: boolean;
}

export const changelogApi = {
  feed: () => api.get<FeedRelease[]>('/api/v1/changelog'),
  state: () => api.get<ChangelogState>('/api/v1/changelog/state', undefined, { toast: false }),
  markSeen: () => api.post<{ ok: true }>('/api/v1/changelog/seen', undefined, { toast: false }),
  markImportantSeen: () =>
    api.post<{ ok: true }>('/api/v1/changelog/important-seen', undefined, { toast: false }),

  pool: (filters: PoolFilters = {}) =>
    api.get<PoolEntry[]>('/api/v1/admin/changelog/pool', {
      ...filters,
      includeHidden: filters.includeHidden ? 'true' : undefined,
      onlyShipped: filters.onlyShipped ? 'true' : undefined,
    }),
  releases: () => api.get<AdminRelease[]>('/api/v1/admin/changelog/releases'),
  releaseEntries: (id: string) =>
    api.get<PoolEntry[]>(`/api/v1/admin/changelog/releases/${id}/entries`),
  createRelease: (body: { title: string; headline?: string | null; body?: string | null }) =>
    api.post<AdminRelease>('/api/v1/admin/changelog/releases', body, {
      toast: { success: 'Черновик выпуска создан' },
    }),
  updateRelease: (
    id: string,
    body: Partial<{
      title: string;
      headline: string | null;
      body: string | null;
      isImportant: boolean;
      hideFromRequesters: boolean;
    }>,
  ) =>
    api.patch<AdminRelease>(`/api/v1/admin/changelog/releases/${id}`, body, {
      toast: { error: false },
    }),
  addEntries: (id: string, entryIds: string[]) =>
    api.post<{ moved: number }>(`/api/v1/admin/changelog/releases/${id}/entries`, { entryIds }),
  removeEntries: (id: string, entryIds: string[]) =>
    api.post<{ returned: number }>(`/api/v1/admin/changelog/releases/${id}/entries/remove`, {
      entryIds,
    }),
  reorder: (id: string, entryIds: string[]) =>
    api.post<{ ordered: number }>(`/api/v1/admin/changelog/releases/${id}/order`, { entryIds }),
  deleteRelease: (id: string) =>
    api.delete<{ deleted: true }>(`/api/v1/admin/changelog/releases/${id}`, {
      toast: { success: 'Выпуск удалён, пункты вернулись в пул' },
    }),
  publish: (
    id: string,
    body: {
      isImportant?: boolean;
      hideFromRequesters?: boolean;
      allowUnshipped?: boolean;
      raiseMinor?: boolean;
    } = {},
  ) =>
    api.post<AdminRelease>(`/api/v1/admin/changelog/releases/${id}/publish`, body, {
      toast: { success: 'Выпуск опубликован' },
    }),
  unpublish: (id: string) =>
    api.post<AdminRelease>(`/api/v1/admin/changelog/releases/${id}/unpublish`, undefined, {
      toast: { success: 'Выпуск снят' },
    }),
  republish: (id: string) =>
    api.post<AdminRelease>(`/api/v1/admin/changelog/releases/${id}/republish`, undefined, {
      toast: { success: 'Выпуск вернулся в ленту' },
    }),
  updateEntry: (
    id: string,
    body: Partial<{
      text: string;
      shortText: string;
      audience: ChangelogAudience;
      isHighlight: boolean;
      detailsOpen: boolean;
      sortOrder: number;
    }>,
  ) =>
    api.patch<PoolEntry>(`/api/v1/admin/changelog/entries/${id}`, body, {
      toast: { error: false },
    }),
  cloneEntry: (
    id: string,
    body: { audience: ChangelogAudience; text: string; shortText?: string },
  ) =>
    api.post<PoolEntry>(`/api/v1/admin/changelog/entries/${id}/clone`, body, {
      toast: { success: 'Копия под другую аудиторию создана' },
    }),
  uploadImage: (file: File): Promise<ChangelogImage> =>
    new Promise((resolve, reject) => {
      Promise.all([getValidToken(), COMPRESSIBLE.has(file.type) ? compressImageFile(file) : file])
        .then(([token, prepared]) => {
          const form = new FormData();
          form.append('file', prepared);
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${config.apiUrl}/api/v1/admin/changelog/images`);
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.onload = () => {
            const fallback =
              xhr.status === 413 ? 'Файл слишком большой' : `Ошибка загрузки (${xhr.status})`;
            try {
              const json = JSON.parse(xhr.responseText) as {
                data?: ChangelogImage;
                error?: { message?: string };
              };
              if (xhr.status >= 200 && xhr.status < 300 && json.data) resolve(json.data);
              else reject(new Error(json.error?.message ?? fallback));
            } catch {
              reject(new Error(fallback));
            }
          };
          xhr.onerror = () => reject(new Error('Нет связи с сервером'));
          xhr.send(form);
        })
        .catch(reject);
    }),

  deleteEntry: (id: string, success: string | false = 'Пункт выброшен из пула') =>
    api.delete<{ deleted: true }>(`/api/v1/admin/changelog/entries/${id}`, {
      toast: success === false ? false : { success },
    }),
};
